import React from "react";
import { FormField } from "@/components/companies/FormField";
import { DropdownField } from "@/components/companies/DropdownField";
import { AddressField } from "@/components/companies/AddressField";
import { useLanguage } from "@/contexts/LanguageContext";
import { FormData } from "@/pages/CompanyForm";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";

interface ValidationField {
  isValid: boolean;
  error: string;
  touched: boolean;
}

interface CompanyInfoSectionProps {
  formData: FormData;
  validation: Record<keyof FormData, ValidationField>;
  formInitialized: boolean;
  basins: { id: string; name: string }[];
  ipmaStations: IPMAStation[];
  basinsLoading: boolean;
  ipmaStationsLoading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (fieldName: string, value: string) => void;
  handleAddressChange: (address: string) => void;
  handleInputBlur: (fieldName: keyof FormData) => void;
  readOnlyFields?: string[];
}

export const CompanyInfoSection = ({
  formData,
  validation,
  formInitialized,
  basins,
  ipmaStations,
  basinsLoading,
  ipmaStationsLoading,
  handleInputChange,
  handleNumberChange,
  handleSelectChange,
  handleAddressChange,
  handleInputBlur,
  readOnlyFields,
}: CompanyInfoSectionProps) => {
  const { t } = useLanguage();

  const activityTypeOptions = [
    { value: "extraction", label: t("activityTypes.extraction") },
    { value: "transformation", label: t("activityTypes.transformation") },
    { value: "extraction_and_transformation", label: t("activityTypes.extraction_and_transformation") },
  ];

  // Get filtered IPMA stations based on selected basin
  const filteredIPMAStations = formData.basinReference
    ? ipmaStations.filter((station) => {
      // Now basinReference contains the basin ID directly
      return !station.isArchived && station.basinId === formData.basinReference;
    })
    : [];

  // Filter basins to only show those that have at least one IPMA station
  const availableBasins = basins.filter((basin) => {
    return ipmaStations.some((station) => 
      !station.isArchived && station.basinId === basin.id
    );
  });

  return (
    <div className="col-span-12 lg:col-span-8">
      <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 space-y-8">
        <h2 className="text-xl font-semibold text-gray-800">{t("dashboard.companyInfo.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {formInitialized && (
            <>
              {/* First row */}
              <FormField
                label={t("companyForm.fields.associateNumber")}
                id="associateNumber"
                type="number"
                value={formData.associateNumber}
                validation={validation.associateNumber}
                onChange={handleNumberChange}
                onBlur={() => handleInputBlur("associateNumber")}
                disabled={readOnlyFields?.includes("associateNumber")}
                required={false}
              />
              <FormField
                label={t("companyForm.fields.name")}
                id="name"
                value={formData.name}
                validation={validation.name}
                onChange={handleInputChange}
                onBlur={() => handleInputBlur("name")}
              />

              {/* Second row */}
              <FormField
                label={t("companyForm.fields.nif")}
                id="nif"
                value={formData.nif}
                validation={validation.nif}
                onChange={handleInputChange}
                onBlur={() => handleInputBlur("nif")}
                disabled={readOnlyFields?.includes("nif")}

              />
              <DropdownField
                label={t("companyForm.fields.activityType")}
                id="activityType"
                value={formData.activityType}
                options={activityTypeOptions}
                validation={validation.activityType}
                onChange={(value) => handleSelectChange("activityType", value)}
                onBlur={() => handleInputBlur("activityType")}
              />

              {/* Third row */}
              <AddressField
                value={formData.address}
                onChange={handleAddressChange}
                label={t("companyForm.fields.address")}
                validation={validation.address}
                onBlur={() => handleInputBlur("address")}
              />

              {/* Fourth row - Basin and Weather Station Selection */}
              {formData.activityType === "extraction_and_transformation" ? (
                // Mixed activity company - show separate extraction and transformation sections
                <div className="md:col-span-2 space-y-6">
                  {/* Extraction Section */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">
                      {t("companyForm.installations.extraction.title")}
                    </h4>
                    <div className="flex flex-col md:flex-row gap-6 items-end">
                      <div className="flex-1">
                        <DropdownField
                          label={t("companyForm.fields.basinReference")}
                          id="extractionBasinReference"
                          value={formData.extractionBasinReference || ""}
                          options={
                            availableBasins && availableBasins.length > 0
                              ? availableBasins.sort((a, b) => a.name.localeCompare(b.name)).map((basin) => ({
                                value: basin.id,
                                label: basin.name,
                              }))
                              : []
                          }
                          validation={validation.extractionBasinReference || { isValid: true, error: "", touched: false }}
                          onChange={(value) => handleSelectChange("extractionBasinReference", value)}
                          onBlur={() => handleInputBlur("extractionBasinReference")}
                          loading={basinsLoading}
                          placeholder={
                            basinsLoading
                              ? t("companyForm.placeholders.loadingBasins")
                              : availableBasins.length === 0
                                ? t("companyForm.placeholders.noBasinsWithStations")
                                : t("companyForm.placeholders.selectBasin")
                          }
                        />
                      </div>
                      <div className="flex-1 w-full">
                        <DropdownField
                          label={t("companyForm.fields.weatherStationReference")}
                          id="extractionWeatherStationReference"
                          value={formData.extractionWeatherStationReference || ""}
                          options={
                            formData.extractionBasinReference
                              ? ipmaStations
                                .filter((station) => station.basinId === formData.extractionBasinReference && !station.isArchived)
                                .map((station) => ({
                                  value: station.id,
                                  label: station.name,
                                }))
                              : []
                          }
                          validation={validation.extractionWeatherStationReference || { isValid: true, error: "", touched: false }}
                          onChange={(value) => handleSelectChange("extractionWeatherStationReference", value)}
                          onBlur={() => handleInputBlur("extractionWeatherStationReference")}
                          loading={ipmaStationsLoading}
                          placeholder={
                            !formData.extractionBasinReference
                              ? t("companyForm.placeholders.selectBasinFirst")
                              : ipmaStationsLoading
                                ? t("companyForm.placeholders.loadingStations")
                                : t("companyForm.placeholders.selectWeatherStation")
                          }
                          disabled={!formData.extractionBasinReference}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Transformation Section */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">
                      {t("companyForm.installations.transformation.title")}
                    </h4>
                    <div className="flex flex-col md:flex-row gap-6 items-end">
                      <div className="flex-1">
                        <DropdownField
                          label={t("companyForm.fields.basinReference")}
                          id="transformationBasinReference"
                          value={formData.transformationBasinReference || ""}
                          options={
                            availableBasins && availableBasins.length > 0
                              ? availableBasins.sort((a, b) => a.name.localeCompare(b.name)).map((basin) => ({
                                value: basin.id,
                                label: basin.name,
                              }))
                              : []
                          }
                          validation={validation.transformationBasinReference || { isValid: true, error: "", touched: false }}
                          onChange={(value) => handleSelectChange("transformationBasinReference", value)}
                          onBlur={() => handleInputBlur("transformationBasinReference")}
                          loading={basinsLoading}
                          placeholder={
                            basinsLoading
                              ? t("companyForm.placeholders.loadingBasins")
                              : availableBasins.length === 0
                                ? t("companyForm.placeholders.noBasinsWithStations")
                                : t("companyForm.placeholders.selectBasin")
                          }
                        />
                      </div>
                      <div className="flex-1 w-full">
                        <DropdownField
                          label={t("companyForm.fields.weatherStationReference")}
                          id="transformationWeatherStationReference"
                          value={formData.transformationWeatherStationReference || ""}
                          options={
                            formData.transformationBasinReference
                              ? ipmaStations
                                .filter((station) => station.basinId === formData.transformationBasinReference && !station.isArchived)
                                .map((station) => ({
                                  value: station.id,
                                  label: station.name,
                                }))
                              : []
                          }
                          validation={validation.transformationWeatherStationReference || { isValid: true, error: "", touched: false }}
                          onChange={(value) => handleSelectChange("transformationWeatherStationReference", value)}
                          onBlur={() => handleInputBlur("transformationWeatherStationReference")}
                          loading={ipmaStationsLoading}
                          placeholder={
                            !formData.transformationBasinReference
                              ? t("companyForm.placeholders.selectBasinFirst")
                              : ipmaStationsLoading
                                ? t("companyForm.placeholders.loadingStations")
                                : t("companyForm.placeholders.selectWeatherStation")
                          }
                          disabled={!formData.transformationBasinReference}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Single activity company - show original single basin/station fields
                <div className="md:col-span-2 flex flex-col md:flex-row gap-6 items-end">
                  <div className="flex-1">
                    <DropdownField
                      label={t("companyForm.fields.basinReference")}
                      id="basinReference"
                      value={formData.basinReference}
                      options={
                        availableBasins && availableBasins.length > 0
                          ? availableBasins.sort((a, b) => a.name.localeCompare(b.name)).map((basin) => ({
                            value: basin.id,
                            label: basin.name,
                          }))
                          : []
                      }
                      validation={validation.basinReference}
                      onChange={(value) => handleSelectChange("basinReference", value)}
                      onBlur={() => handleInputBlur("basinReference")}
                      loading={basinsLoading}
                      placeholder={
                        basinsLoading
                          ? t("companyForm.placeholders.loadingBasins")
                          : availableBasins.length === 0
                            ? t("companyForm.placeholders.noBasinsWithStations")
                            : t("companyForm.placeholders.selectBasin")
                      }
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <DropdownField
                      label={t("companyForm.fields.weatherStationReference")}
                      id="weatherStationReference"
                      value={formData.weatherStationReference}
                      options={
                        filteredIPMAStations.length > 0
                          ? filteredIPMAStations.map((station) => ({
                            value: station.id,
                            label: station.name,
                          }))
                          : []
                      }
                      validation={validation.weatherStationReference}
                      onChange={(value) => handleSelectChange("weatherStationReference", value)}
                      onBlur={() => handleInputBlur("weatherStationReference")}
                      loading={ipmaStationsLoading}
                      placeholder={
                        !formData.basinReference
                          ? t("companyForm.placeholders.selectBasinFirst")
                          : ipmaStationsLoading
                            ? t("companyForm.placeholders.loadingStations")
                            : filteredIPMAStations.length === 0
                              ? t("companyForm.placeholders.noStationsForBasin")
                              : t("companyForm.placeholders.selectWeatherStation")
                      }
                      disabled={!formData.basinReference || filteredIPMAStations.length === 0}
                    />
                  </div>
                </div>
              )}

              {/* Fifth row */}
              <FormField
                label={t("companyForm.fields.manager")}
                id="calculationManager"
                value={formData.calculationManager}
                validation={validation.calculationManager}
                onChange={handleInputChange}
                onBlur={() => handleInputBlur("calculationManager")}
              />
              <FormField
                label={t("companyForm.fields.managerEmail")}
                id="calculationManagerEmail"
                type="email"
                value={formData.calculationManagerEmail}
                validation={validation.calculationManagerEmail}
                onChange={handleInputChange}
                onBlur={() => handleInputBlur("calculationManagerEmail")}
              />
            </>
          )}
        </div>
        {/* Required Field Legend */}
        <div className="text-xs text-gray-500 italic mt-4">
          {t("common.requiredFieldLegend")}
        </div>
      </div>
    </div>
  );
};
